<template>
    <ModalBox
        :is-active.sync="isModalActive"
        @close="empty"
        @submit.stop.prevent="submit">
        <template #header>
            <h5 class="title is-5">
                {{ $t(`admin.finances.costs.modal.header`) }}
            </h5>
        </template>
        <div class="columns">
            <div class="column">
                <b-field grouped>
                    <slot
                        :modal="modal"
                        name="user"></slot>
                </b-field>
                <b-field
                    v-if="withPaymentSystem"
                    :label="$t(`admin.finances.costs.modal.body.paymentSystem.title`)">
                    <Select
                        v-model="modal.paymentSystemId"
                        :get-data-vuex="getPaymentSystems"
                        :placeholder="$t(`admin.finances.costs.modal.body.paymentSystem.label`)"
                        field="name"
                        :min-required-length="1"
                        @select="option => modal.paymentSystem = (option || { id: null })">
                    </Select>
                </b-field>
                <b-field
                    v-if="withRequisites"
                    :label="$t(`admin.finances.costs.modal.body.requisite.title`)">
                    <Select
                        v-model="modal.requisite.purse"
                        :disabled="isDisabledRequisites"
                        :get-data-vuex="getRequisites"
                        :placeholder="$t(`admin.finances.costs.modal.body.requisite.label`)"
                        :seed="requisitesParams"
                        expanded
                        field="purse"
                        open-on-focus
                        :required="modal.paymentSystem.isRequisite && role !== 'admin'"
                        :selectable="({ status }) => status === 'APPROVED'"
                        @select="option => modal.requisite.id = (option || { id: null }).id">
                        <template slot-scope="{ option }">
                            <div
                                class="media"
                                :class="{'is-disabled' : option.status !== 'APPROVED'}">
                                <div class="media-content">
                                    {{ option.purse }}
                                    <br>
                                    <small>{{ option.type }}</small>
                                </div>
                            </div>
                        </template>
                    </Select>
                </b-field>
                <div class="columns">
                    <div class="column">
                        <b-field :label="$t(`admin.finances.costs.modal.body.sum.title`)">
                            <LInput
                                v-model.number="modal.amount"
                                min="1"
                                step="0.01"
                                type="number"></LInput>
                        </b-field>
                    </div>
                    <div class="column is-narrow">
                        <b-field :label="$t(`admin.finances.costs.modal.body.sum.currency`)">
                            <Select
                                :value="currency"
                                disabled
                                :get-data="getCurrency">
                            </Select>
                        </b-field>
                    </div>
                </div>
                <b-field :label="$t(`admin.finances.costs.modal.body.comment`)">
                    <LInput
                        v-model="modal.comment"
                        maxlength="200"
                        type="textarea"></LInput>
                </b-field>
                <!--                <b-field class="file">-->
                <!--                    <b-upload v-model="file">-->
                <!--                        <div class="button is-primary">-->
                <!--                            <b-icon icon="upload" size="is-small"></b-icon>-->
                <!--                            <span>Click to upload</span>-->
                <!--                        </div>-->
                <!--                    </b-upload>-->
                <!--                    <span v-if="file" class="file-name">-->
                <!--                        {{ file.name }}-->
                <!--                    </span>-->
                <!--                </b-field>-->
            </div>
        </div>
        <template #footer>
            <div class="container">
                <div class="columns">
                    <div class="column">
                        <b-button
                            :disabled="isDisabledConfirm"
                            :loading="isLoading"
                            class="is-fullwidth is-success is-light"
                            native-type="submit">
                            {{ $t(`admin.finances.costs.modal.footer.confirm`) }}
                        </b-button>
                    </div>
                </div>
            </div>
        </template>
    </ModalBox>
</template>

<script>
  import Select from "@/components/Common/Select/Select.vue";
  import { Modal } from "@core/mixins";
  import {
    GET_PAYMENT_SYSTEMS,
    GET_REQUISITES_LIST
  } from "@core/store/action-constants";
  import {
    UPDATE_PAYMENT_SYSTEMS_FILTERS,
    UPDATE_PAYMENT_SYSTEMS_LIST_PAGINATION,
    UPDATE_REQUISITES_FILTERS,
    UPDATE_REQUISITES_LIST_PAGINATION
  } from "@core/store/mutation-constants";
  import { mapActions, mapMutations, mapState } from "vuex";
  import ModalBox from "@/components/Common/Modals/ModalBox.vue";
  
  const initModal = () => {
    return {
      user: {
        id: null,
        login: null
      },
      requisite: {
        id: null,
        purse: null
      },
      amount: 0,
      paymentSystem: {
        id: null,
        name: null,
        isRequisite: false
      },
      comment: "",
      file: null
    };
  };

  export default {
    name: "PaymentModal",
    mixins: [Modal],
    components: {
      ModalBox,
      Select
    },
    props: {
      userId: {
        type: String,
        default: null
      },
    
      withRequisites: {
        type: Boolean,
        default: true
      },

      withPaymentSystem: {
        type: Boolean,
        default: true
      }
    },

    data () {
      return {
        modal: initModal()
      };
    },
    computed: {
      ...mapState("admin/finances", {
        requisitesList: ({ requisitesList }) => requisitesList.data || {}
      }),

      ...mapState({
        role: ({ auth }) => auth.role
      }),

      ...mapState("admin/finances/paymentSystemsList", {
        paymentSystems: ({ data }) => data
      }),

      ...mapState({
        currencyList: ({ auth }) => auth?.availableCurrencies
      }),

      currency () {
        return this.modal.paymentSystem?.currency;
      },

      isDisabledRequisites () {
        return !this.isActive || !this.modal.user.id || this.withPaymentSystem && !this.modal.paymentSystem.id;
      },

      isDisabledConfirm () {
        return !this.modal.user.id || this.withPaymentSystem && !this.modal.paymentSystem.id || !this.modal.amount;
      },

      requisitesParams () {
        const { user: { id: userId }, paymentSystem: { id: paymentSystemId } } = this.modal;
        return { userId, paymentSystemId };
      }
    },
    methods: {
      ...mapMutations("admin/finances", {
        UPDATE_REQUISITES_LIST_PAGINATION: `requisitesList/${ UPDATE_REQUISITES_LIST_PAGINATION }`,
        UPDATE_REQUISITES_FILTERS: `requisitesList/${ UPDATE_REQUISITES_FILTERS }`,
        UPDATE_PAYMENT_SYSTEMS_LIST_PAGINATION: `paymentSystemsList/${ UPDATE_PAYMENT_SYSTEMS_LIST_PAGINATION }`,
        UPDATE_PAYMENT_SYSTEMS_FILTERS: `paymentSystemsList/${ UPDATE_PAYMENT_SYSTEMS_FILTERS }`
      }),

      ...mapActions("admin/finances", {
        GET_PAYMENT_SYSTEMS: `paymentSystemsList/${ GET_PAYMENT_SYSTEMS }`,
        GET_REQUISITES_LIST: `requisitesList/${ GET_REQUISITES_LIST }`
      }),

      submit () {
        this.$emit("submit", this.modal);
      },

      async getRequisites () {
        if (!this.isDisabledRequisites) {
          this.UPDATE_REQUISITES_LIST_PAGINATION();
          this.UPDATE_REQUISITES_FILTERS(this.requisitesParams);
          await this.GET_REQUISITES_LIST();
          return this.requisitesList;
        }
      },

      async getPaymentSystems (label = "") {
        this.UPDATE_PAYMENT_SYSTEMS_LIST_PAGINATION();
        this.UPDATE_PAYMENT_SYSTEMS_FILTERS({ name: label.length > 0 ? label : null, withAttachedToUser: null });
        await this.GET_PAYMENT_SYSTEMS();
        return this.paymentSystems;
      },

      empty () {
        this.modal = initModal();
        this.$emit("close");
      },

      getCurrency () {
        const items = ["USD", "RUB", "EUR"];
        return {
          items,
          count: items.length
        };
      }
    },
    watch: {
      "modal.user": {
        deep: true,
        handler (value) {
          this.$emit("update:user", value);
        }
      },
      isActive (value) {
        if (value) {
          this.modal = initModal();
        }
        this.modal.user.id = this.userId;
      }
    }
  };
</script>

<style lang="scss" scoped>
    .media.is-disabled {
        color: $light;
        cursor: not-allowed;
    }
</style>
